import React, { useEffect, useMemo, useState, useRef } from 'react';
import { CalculatorInputs } from '../components/CalculatorInputs';
import { ResultsSummary } from '../components/ResultsSummary';
import { ProfitChart } from '../components/ProfitChart';
import { ProjectionTable } from '../components/ProjectionTable';
import { ShareSummary } from '../components/ShareSummary';
import { Inputs, DEFAULT_INPUTS, calculateModel, PackageType, BuildPayType, IndustryType } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
const CORRECT_PASSWORD = 'HIRE100k';
const AUTH_KEY = 'hire_calculator_authenticated';
export function ROICalculator() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [inputs, setInputs] = useState<Inputs>(DEFAULT_INPUTS);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [copySummaryLabel, setCopySummaryLabel] = useState('Copy Summary');
  const [savePdfLabel, setSavePdfLabel] = useState('Save as PDF');
  const [showPackageTooltip, setShowPackageTooltip] = useState(false);
  const [showBuildTooltip, setShowBuildTooltip] = useState(false);
  const [expandedReviewSections, setExpandedReviewSections] = useState({
    campaignAdSpend: false,
    revenueMargins: false,
    salesPerformance: false,
    retentionModeling: false
  });
  const resultsRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLDivElement>(null);
  // Check authentication on mount (client-side only)
  useEffect(() => {
    setIsClient(true);
    const authStatus = localStorage.getItem(AUTH_KEY);
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);
  // Load state from URL on mount
  useEffect(() => {
    if (!isClient) return;
    const params = new URLSearchParams(window.location.search);
    if ([...params.keys()].length === 0) return;
    const newInputs = {
      ...DEFAULT_INPUTS
    };
    const parseNum = (key: string, def: number) => {
      const val = params.get(key);
      return val ? parseFloat(val) : def;
    };
    if (params.has('pkg')) newInputs.pkg = params.get('pkg') as PackageType;
    if (params.has('buildPay')) newInputs.buildPay = params.get('buildPay') as BuildPayType;
    if (params.has('industry')) newInputs.industry = params.get('industry') as IndustryType;
    newInputs.campaigns = parseNum('campaigns', DEFAULT_INPUTS.campaigns);
    newInputs.adSpend = parseNum('adSpend', DEFAULT_INPUTS.adSpend);
    newInputs.cph = parseNum('cph', DEFAULT_INPUTS.cph);
    newInputs.ticket = parseNum('ticket', DEFAULT_INPUTS.ticket);
    newInputs.gpm = parseNum('gpm', DEFAULT_INPUTS.gpm);
    newInputs.salesVetMonthly = parseNum('salesVetMonthly', DEFAULT_INPUTS.salesVetMonthly);
    newInputs.salesFirst90Total = parseNum('salesFirst90Total', DEFAULT_INPUTS.salesFirst90Total);
    newInputs.pctLaunched = parseNum('pctLaunched', DEFAULT_INPUTS.pctLaunched);
    newInputs.pct30 = parseNum('pct30', DEFAULT_INPUTS.pct30);
    newInputs.pct90 = parseNum('pct90', DEFAULT_INPUTS.pct90);
    newInputs.pct1y = parseNum('pct1y', DEFAULT_INPUTS.pct1y);
    newInputs.pullthrough = parseNum('pullthrough', DEFAULT_INPUTS.pullthrough);
    setInputs(newInputs);
    if (params.has('calculated') && params.get('calculated') === 'true') {
      setHasCalculated(true);
    }
  }, [isClient]);
  // Update URL when inputs change
  useEffect(() => {
    if (!isClient) return;
    const params = new URLSearchParams();
    Object.entries(inputs).forEach(([key, value]) => {
      params.set(key, String(value));
    });
    if (hasCalculated) {
      params.set('calculated', 'true');
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [inputs, hasCalculated, isClient]);
  // Calculate results
  const results = useMemo(() => {
    if (!inputs.pkg || !inputs.buildPay) {
      return null;
    }
    return calculateModel(inputs);
  }, [inputs]);
  const toggleReviewSection = (section: keyof typeof expandedReviewSections) => {
    setExpandedReviewSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem(AUTH_KEY, 'true');
      setError('');
    } else {
      setError('Incorrect access code. Please try again.');
      setPassword('');
    }
  };
  const handleCalculate = () => {
    setHasCalculated(true);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  const handleReview = () => {
    setHasReviewed(true);
    setTimeout(() => {
      reviewRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  const handleReset = () => {
    setInputs(DEFAULT_INPUTS);
    setHasCalculated(false);
    setHasReviewed(false);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleCopySummary = async () => {
    const textarea = document.querySelector('textarea') as HTMLTextAreaElement;
    if (textarea) {
      try {
        await navigator.clipboard.writeText(textarea.value);
        setCopySummaryLabel('Copied ✓');
        setTimeout(() => setCopySummaryLabel('Copy Summary'), 1200);
      } catch (err) {
        console.error('Failed to copy', err);
      }
    }
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyLinkLabel('Link Copied✓');
      setTimeout(() => setCopyLinkLabel('Copy Share Link'), 1200);
    } catch (err) {
      console.error('Failed to copy link', err);
    }
  };
  const handleSavePdf = async () => {
    setSavePdfLabel('Generating PDF...');
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      const element = resultsRef.current;
      if (!element) return;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      const companyName = inputs.companyName || 'Company';
      const fileName = `${companyName.replace(/\s+/g, '_')}_ROI_Projection.pdf`;
      pdf.save(fileName);
      setSavePdfLabel('PDF Saved ✓');
      setTimeout(() => setSavePdfLabel('Save as PDF'), 2000);
    } catch (err) {
      console.error('Failed to generate PDF', err);
      setSavePdfLabel('Failed - Try Again');
      setTimeout(() => setSavePdfLabel('Save as PDF'), 2000);
    }
  };
  // Calculated values
  const adSpendTotal = inputs.adSpend * inputs.campaigns;
  const estimatedHires = Math.round(adSpendTotal / inputs.cph);
  const estimatedLaunched = estimatedHires * (inputs.pctLaunched / 100);
  const estimatedActive30 = estimatedLaunched * (inputs.pct30 / 100);
  const estimatedActive90 = estimatedActive30 * (inputs.pct90 / 100);
  const estimatedActive1Year = estimatedActive90 * (inputs.pct1y / 100);
  const gpPerSale = inputs.ticket * (inputs.gpm / 100);
  const rookieTotalSales = inputs.salesFirst90Total;
  const rookieMonthlyRevenue = estimatedActive90 * rookieTotalSales * inputs.ticket * (inputs.pullthrough / 100) / 3;
  const veteranMonthlyRevenue = estimatedActive1Year * inputs.salesVetMonthly * inputs.ticket * (inputs.pullthrough / 100);
  const canCalculate = inputs.pkg && inputs.buildPay;
  // Show loading during initial mount to prevent hydration mismatch
  if (!isClient) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white" data-id="element-241"></div>;
  }
  // If not authenticated, show login screen
  if (!isAuthenticated) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-4" data-id="element-242">
        <div className="w-full max-w-md" data-id="element-243">
          <div className="text-center mb-8" data-id="element-244">
            <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-12 mx-auto mb-6" data-id="element-245" />
            <h1 className="text-2xl font-bold text-navy mb-2" data-id="element-246">
              ROI Calculator Access
            </h1>
            <p className="text-gray-600" data-id="element-247">Enter your access code to continue</p>
          </div>

          <form onSubmit={handleLogin} className="bg-white border-2 border-gray-200 rounded-lg p-8 shadow-lg" data-id="element-248">
            <div className="mb-6" data-id="element-249">
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-250">
                Access Code
              </label>
              <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full p-3 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors font-mono" placeholder="Enter code" autoFocus data-id="element-251" />
              {error && <p className="mt-2 text-sm text-red-600" data-id="element-252">{error}</p>}
            </div>

            <button type="submit" className="w-full py-3 rounded bg-amber hover:bg-amber-dark text-navy font-bold text-base transition-colors shadow-md" data-id="element-253">
              Access Calculator
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6" data-id="element-254">
            Your access will be saved for future visits
          </p>
        </div>
      </div>;
  }
  // Main calculator content
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white" data-id="element-255">
      {/* Header with Logo */}
      <header className="border-b-2 border-gray-200 bg-white" data-id="element-256">
        <div className="max-w-[1600px] mx-auto px-4 py-4 md:py-6 flex items-center justify-between" data-id="element-257">
          <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8 md:h-12" data-id="element-258" />
          <a href="https://project-hiredeck.magicpatterns.app/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-semibold text-sm transition-colors shadow-md" data-id="element-259">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-260">
              <rect x="3" y="3" width="7" height="7" data-id="element-261" />
              <rect x="14" y="3" width="7" height="7" data-id="element-262" />
              <rect x="14" y="14" width="7" height="7" data-id="element-263" />
              <rect x="3" y="14" width="7" height="7" data-id="element-264" />
            </svg>
            Deck
          </a>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 py-8 md:py-12 pb-16" data-id="element-265">
        {/* Hero Section */}
        <div className="mb-12 md:mb-16 text-center" data-id="element-266">
          <h1 className="text-3xl md:text-4xl lg:text-[44px] font-bold text-navy leading-tight mb-4 tracking-tight" data-id="element-267">
            Recruiting ROI Calculator
          </h1>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto" data-id="element-268">
            Enter your business assumptions below to calculate your 12-month ROI
            projection. See exactly how HIRE delivers predictable recruiting
            results.
          </p>
        </div>

        {/* Step 1: Input Form */}
        <div className="mb-8 md:mb-12" data-id="element-269">
          <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-270">
            <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-271">
              1
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-272">
              Business Assumptions
            </h2>
          </div>
          <CalculatorInputs inputs={inputs} onChange={setInputs} onReset={handleReset} onReview={handleReview} showActionButtons={false} data-id="element-273" />
        </div>

        {/* Step 2: Validation Summary */}
        {hasReviewed && <div ref={reviewRef} className="mb-8 md:mb-12" data-id="element-274">
            <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-275">
              <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-276">
                2
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-277">
                Review Your Numbers
              </h2>
            </div>
            <div className="bg-white border-2 border-gray-200 rounded p-6 md:p-8 shadow-lg" data-id="element-278">
              {/* Monthly Ad Investment */}
              <div className="mb-8" data-id="element-279">
                <div className="p-4 md:p-6 rounded bg-gray-50 border-2 border-gray-200" data-id="element-280">
                  <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-281">
                    Monthly Ad Investment
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-navy" data-id="element-282">
                    {fmtMoney(adSpendTotal)}
                  </div>
                  <div className="text-sm text-gray-600 mt-2" data-id="element-283">
                    {inputs.campaigns} campaign{inputs.campaigns > 1 ? 's' : ''}{' '}
                    × {fmtMoney(inputs.adSpend)}
                  </div>
                </div>
              </div>

              {/* Retention Funnel Visualization */}
              <div className="mb-8" data-id="element-284">
                <div className="text-base font-semibold text-navy mb-4" data-id="element-285">
                  Expected Monthly Retention Funnel
                </div>
                <div className="space-y-3" data-id="element-286">
                  {/* Hires */}
                  <div className="flex items-center gap-3" data-id="element-287">
                    <div className="w-20 text-right" data-id="element-288">
                      <span className="text-lg font-bold text-navy" data-id="element-289">
                        {estimatedHires}
                      </span>
                    </div>
                    <div className="w-28 text-sm font-semibold text-gray-700" data-id="element-290">
                      Hires
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-291">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-navy to-navy/90 rounded-full" style={{
                    width: '100%'
                  }} data-id="element-292" />
                    </div>
                  </div>

                  {/* Launched */}
                  <div className="flex items-center gap-3" data-id="element-293">
                    <div className="w-20 text-right" data-id="element-294">
                      <span className="text-lg font-bold text-navy" data-id="element-295">
                        {estimatedLaunched.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-sm font-semibold text-gray-700" data-id="element-296">
                      Launched
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-297">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber to-amber/90 rounded-full" style={{
                    width: `${estimatedLaunched / estimatedHires * 100}%`
                  }} data-id="element-298" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-299">
                      {inputs.pctLaunched}%
                    </div>
                  </div>

                  {/* Active 30d */}
                  <div className="flex items-center gap-3" data-id="element-300">
                    <div className="w-20 text-right" data-id="element-301">
                      <span className="text-lg font-bold text-navy" data-id="element-302">
                        {estimatedActive30.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-sm font-semibold text-gray-700" data-id="element-303">
                      Active 30d
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-304">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full" style={{
                    width: `${estimatedActive30 / estimatedHires * 100}%`
                  }} data-id="element-305" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-306">
                      {inputs.pct30}%
                    </div>
                  </div>

                  {/* Active 90d */}
                  <div className="flex items-center gap-3" data-id="element-307">
                    <div className="w-20 text-right" data-id="element-308">
                      <span className="text-lg font-bold text-navy" data-id="element-309">
                        {estimatedActive90.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-sm font-semibold text-gray-700" data-id="element-310">
                      Active 90d
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-311">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 to-green-400 rounded-full" style={{
                    width: `${estimatedActive90 / estimatedHires * 100}%`
                  }} data-id="element-312" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-313">
                      {inputs.pct90}%
                    </div>
                  </div>

                  {/* Active 1 Year */}
                  <div className="flex items-center gap-3" data-id="element-314">
                    <div className="w-20 text-right" data-id="element-315">
                      <span className="text-lg font-bold text-navy" data-id="element-316">
                        {estimatedActive1Year.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-sm font-semibold text-gray-700" data-id="element-317">
                      Active 1 Year
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-318">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-purple-400 rounded-full" style={{
                    width: `${estimatedActive1Year / estimatedHires * 100}%`
                  }} data-id="element-319" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-320">
                      {inputs.pct1y}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Deal Economics - Prominent Display */}
              <div className="mb-8" data-id="element-321">
                <div className="text-base font-semibold text-navy mb-4" data-id="element-322">
                  Deal Economics
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-323">
                  {/* Average Deal Size */}
                  <div className="p-4 md:p-6 rounded bg-gray-50 border-2 border-gray-200" data-id="element-324">
                    <div className="text-xs text-gray-600 mb-2 font-semibold uppercase tracking-wide" data-id="element-325">
                      Avg Deal Size
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-navy" data-id="element-326">
                      {fmtMoney(inputs.ticket)}
                    </div>
                  </div>

                  {/* Profit Per Sale */}
                  <div className="p-4 md:p-6 rounded bg-green-50 border-2 border-green-200" data-id="element-327">
                    <div className="text-xs text-gray-600 mb-2 font-semibold uppercase tracking-wide" data-id="element-328">
                      Profit Per Sale
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-green-600" data-id="element-329">
                      {fmtMoney(gpPerSale)}
                    </div>
                    <div className="text-xs text-gray-600 mt-1" data-id="element-330">
                      {inputs.gpm}% margin
                    </div>
                  </div>

                  {/* Realization Rate */}
                  <div className="p-4 md:p-6 rounded bg-blue-50 border-2 border-blue-200" data-id="element-331">
                    <div className="text-xs text-gray-600 mb-2 font-semibold uppercase tracking-wide" data-id="element-332">
                      Realization Rate
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-blue-600" data-id="element-333">
                      {inputs.pullthrough}%
                    </div>
                    <div className="text-xs text-gray-600 mt-1" data-id="element-334">
                      Pullthrough
                    </div>
                  </div>
                </div>
              </div>

              {/* Sales Performance Impact */}
              <div className="border-t-2 border-gray-200 pt-6 mb-6" data-id="element-335">
                <div className="text-base font-semibold text-navy mb-4" data-id="element-336">
                  Expected Sales Performance
                </div>

                <div className="space-y-4" data-id="element-337">
                  {/* Rookies */}
                  <div className="p-4 rounded bg-blue-50 border-2 border-blue-200" data-id="element-338">
                    <div className="text-sm font-bold text-navy mb-3" data-id="element-339">
                      Total Sales First 90 Days per Monthly Recruiting Class
                    </div>
                    <div className="text-base text-gray-700 mb-2" data-id="element-340">
                      {estimatedActive90.toFixed(1)} active reps ×{' '}
                      {inputs.salesFirst90Total} sales each
                    </div>
                    <div className="text-xl font-bold text-navy" data-id="element-341">
                      {(estimatedActive90 * rookieTotalSales).toFixed(1)} total
                      sales
                    </div>
                  </div>

                  {/* Veterans */}
                  <div className="p-4 rounded bg-purple-50 border-2 border-purple-200" data-id="element-342">
                    <div className="text-sm font-bold text-navy mb-3" data-id="element-343">
                      Monthly Sales per Veteran (Day 91+)
                    </div>
                    <div className="text-base text-gray-700 mb-2" data-id="element-344">
                      {estimatedActive1Year.toFixed(1)} veteran reps ×{' '}
                      {inputs.salesVetMonthly} sales/month
                    </div>
                    <div className="text-xl font-bold text-navy" data-id="element-345">
                      {(estimatedActive1Year * inputs.salesVetMonthly).toFixed(1)}{' '}
                      sales/month
                    </div>
                  </div>
                </div>
              </div>

              <button onClick={handleCalculate} disabled={!canCalculate} className={`w-full py-4 md:py-5 rounded font-bold text-base md:text-lg transition-all shadow-lg ${canCalculate ? 'bg-amber hover:bg-amber-dark text-navy active:translate-y-[1px] cursor-pointer' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-346">
                {canCalculate ? 'Calculate My ROI →' : 'Please select Partnership in Step 1'}
              </button>
            </div>
          </div>}

        {/* Step 3: Results */}
        {hasCalculated && results && <div ref={resultsRef} className="scroll-mt-8" data-id="element-347">
            <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-348">
              <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-349">
                3
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-350">
                Projected Investment and ROI
              </h2>
            </div>

            <div className="mb-4" data-id="element-351">
              <div className="inline-block bg-amber-light text-amber-dark px-4 py-2 rounded text-sm font-semibold border-2 border-amber/20" data-id="element-352">
                {results.preset.name} •{' '}
                {inputs.buildPay === 'upfront' ? 'Standard Payment' : '2 Monthly Payments'}
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded p-6 md:p-8 shadow-lg mb-6" data-id="element-353">
              <ResultsSummary results={results} data-id="element-354" />
            </div>

            <div className="mb-6" data-id="element-355">
              <ProjectionTable rows={results.rows} inputs={inputs} onInputsChange={setInputs} data-id="element-356" />
            </div>

            <div className="mb-6" data-id="element-357">
              <ProfitChart rows={results.rows} breakeven={results.breakeven} data-id="element-358" />
            </div>

            <div className="flex flex-wrap gap-3 mb-6" data-id="element-359">
              <button onClick={handleCopySummary} className="px-3 py-2 rounded border-2 border-navy text-navy hover:bg-navy hover:text-white font-semibold text-xs transition-colors active:translate-y-[1px]" data-id="element-360">
                {copySummaryLabel}
              </button>

              <button onClick={handleReset} className="px-3 py-2 rounded border-2 border-gray-300 text-gray-700 hover:border-gray-400 font-semibold text-xs transition-colors active:translate-y-[1px]" data-id="element-361">
                Start Over
              </button>
            </div>

            <div className="mb-6" data-id="element-362">
              <ShareSummary inputs={inputs} results={results} data-id="element-363" />
            </div>
          </div>}
      </div>
    </div>;
}